import { useState, useRef, useEffect } from 'react';
import { buildSignedPdfHtml } from '../../lib/invoiceHelpers';
import { Row } from '../shared/UI';

export default function SignaturePage(){
  const params = new URLSearchParams(window.location.search);
  const token  = params.get('t');   // short token mode: /sign?t=K7M2X9PQ

  const [payload,       setPayload]       = useState(null);
  const [loadError,     setLoadError]     = useState(false);
  const [loadingToken,  setLoadingToken]  = useState(!!token);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [paidAt,        setPaidAt]        = useState(null);
  const [serverSignatureDataUrl, setServerSignatureDataUrl] = useState(null);
  const [expiresAt,     setExpiresAt]     = useState(null);
  const [linkExpired,   setLinkExpired]   = useState(false);

  const canvasRef = useRef(null);
  const [signed,   setSigned]   = useState(false);
  const [hasInk,   setHasInk]   = useState(false);
  const drawing = useRef(false);
  const last    = useRef({x:0,y:0});

  const GOLD = "#ac9055";
  const NAVY = "#202a44";

  // ── Load payload ──────────────────────────────────────────────────────────
  useEffect(()=>{
    if(token){
      // Token mode: fetch from serverless API
      fetch(`/api/sign?t=${encodeURIComponent(token)}`)
        .then(r => r.json())
        .then(data => {
          if(data?.ok && data.payload){
            setPayload(data.payload);
            if(data.alreadySigned)    setAlreadySigned(true);
            if(data.paidAt)           setPaidAt(data.paidAt);
            if(data.signatureDataUrl) setServerSignatureDataUrl(data.signatureDataUrl);
            if(data.expiresAt)        setExpiresAt(data.expiresAt);
            if(data.expired)          setLinkExpired(true);
          } else {
            setLoadError(true);
          }
        })
        .catch(()=> setLoadError(true))
        .finally(()=> setLoadingToken(false));
    } else {
      // Legacy /sign?d=<base64> links are no longer accepted. They carried the
      // customer's name, phone and national ID in the URL, readable by anyone who
      // saw the link. Every current link uses a short token instead.
      setLoadError(true);
    }
  }, []); // eslint-disable-line

  // ── Signature pad logic ───────────────────────────────────────────────────
  function getPos(e){
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX-rect.left, y: t.clientY-rect.top };
  }
  function start(e){ e.preventDefault(); drawing.current=true; last.current=getPos(e); setHasInk(true); }
  function move(e){
    if(!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p   = getPos(e);
    ctx.strokeStyle = NAVY;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  }
  function end(){ drawing.current=false; }
  function clearPad(){
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0,0,c.width,c.height);
    setHasInk(false);
  }

  const [uploadStatus, setUploadStatus] = useState("idle");
  const [savedSig,    setSavedSig]    = useState(null);
  const [signedPdfUrl, setSignedPdfUrl] = useState(null); // blob URL for inline preview

  // ── Upload signature ───────────────────────────────────────────────────────
  async function uploadSignature(sigDataUrl){
    // Token mode: POST to /api/sign (credentials stay server-side)
    if(token){
      try {
        const r = await fetch('/api/sign', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            token,
            signature: sigDataUrl,
            signedAt:  new Date().toISOString(),
          }),
        });
        const data = await r.json();
        return data?.ok === true;
      } catch(e){ console.error('signature upload failed:', e); return false; }
    }
    // No token means a legacy link. Those wrote to the database straight from the
    // customer's browser using the shared key — the path this whole change removes.
    console.error('signature upload refused: no token (legacy links are disabled)');
    return false;
  }

  async function handleSubmit(){
    if(!hasInk){ alert('يرجى التوقيع أولاً'); return; }
    const sigDataUrl = canvasRef.current.toDataURL('image/png');
    setSavedSig(sigDataUrl);
    // Build PDF blob URL before unmounting canvas
    const pdfUrl = buildPdfBlobUrl(payload, sigDataUrl);
    setSignedPdfUrl(pdfUrl);
    setSigned(true);
    setUploadStatus('uploading');
    const ok = await uploadSignature(sigDataUrl);
    setUploadStatus(ok ? 'done' : 'error');
  }

  function buildPdfBlobUrl(p, sigDataUrl){
    const html = buildSignedPdfHtml(p, sigDataUrl);
    const blob = new Blob([html], {type:'text/html;charset=utf-8'});
    return URL.createObjectURL(blob);
  }

  function downloadSignedPdf(){
    // Open in a new window and trigger the browser's print dialog — the user
    // picks "Save as PDF" to get a real PDF file. Same pattern as the new-invoice
    // download flow in Register.js.
    const url = signedPdfUrl || (savedSig && payload ? buildPdfBlobUrl(payload, savedSig) : null);
    if(!url) return;
    const win = window.open(url,'_blank');
    if(win) setTimeout(()=>{ win.print(); }, 800);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const containerStyle = {
    minHeight:'100vh', background:'#f8f6f0',
    fontFamily:"'Tajawal',sans-serif",
    display:'flex', alignItems:'center', justifyContent:'center', padding:24,
  };
  const cardStyle = {
    background:'#fff', padding:'40px 32px', borderRadius:14,
    maxWidth:420, textAlign:'center', border:'1px solid #e8e0cc',
  };

  if(loadingToken){
    return (
      <div dir="rtl" style={containerStyle}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');`}</style>
        <div style={cardStyle}>
          <div style={{fontSize:40,marginBottom:12}}>⏳</div>
          <h2 style={{color:NAVY,fontSize:18}}>جاري تحميل الفاتورة…</h2>
        </div>
      </div>
    );
  }

  // Note: 'already signed' state is no longer handled by an early return.
  // The main render below routes through isPaidReceipt / isWaitingForPay / sign form
  // based on signed + paidAt — so a customer re-opening their link sees the right view.

  if(loadError || !payload){
    return (
      <div dir="rtl" style={containerStyle}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');`}</style>
        <div style={cardStyle}>
          <div style={{fontSize:48,marginBottom:12}}>⚠️</div>
          <h2 style={{color:NAVY,marginBottom:10,fontSize:20}}>رابط غير صالح</h2>
          <p style={{color:'#79858d',fontSize:13,lineHeight:1.7}}>
            هذا الرابط لا يحتوي على بيانات فاتورة صالحة. تواصل مع FindRE للحصول على رابط جديد.
          </p>
        </div>
      </div>
    );
  }

  const fmt   = n => Number(n||0).toLocaleString();
  const total = (Number(payload.rent)||0) + (Number(payload.deposit)||0);

  // Three view states (in priority order):
  //   1) signed + paid → "Final paid receipt" with downloadable signed PDF
  //   2) signed but not paid → "Wait for payment" message
  //   3) not signed → Show sign form
  const effectiveSig    = savedSig || serverSignatureDataUrl;
  const isSigned        = signed || alreadySigned;
  const isPaidReceipt   = isSigned && paidAt && effectiveSig;
  const isWaitingForPay = isSigned && !isPaidReceipt;

  function downloadPaidReceipt(){
    if(!effectiveSig) return;
    // Pass paidAt so the PDF renders with the PAID stamp.
    const html = buildSignedPdfHtml({ ...payload, paidAt }, effectiveSig);
    const blob = new Blob([html],{type:'text/html;charset=utf-8'});
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url,'_blank');
    if(win) setTimeout(()=>{ win.print(); }, 800);
  }

  // Pre-built WhatsApp link (used by floating button + footer button)
  const waContactUrl = `https://wa.me/966551817337?text=${encodeURIComponent(`مرحباً FindRE، لدي استفسار${payload?.invoiceNo?` بخصوص فاتورة ${payload.invoiceNo}`:''}`)}`;

  return(
    <div dir="rtl" style={{minHeight:'100vh',background:'#f8f6f0',fontFamily:"'Tajawal',sans-serif",padding:'20px 14px'}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');`}</style>

      {/* Floating WhatsApp button — sticks to the bottom-left, follows scroll */}
      <a href={waContactUrl} target="_blank" rel="noopener noreferrer" aria-label="تواصل عبر واتساب"
        style={{position:'fixed',bottom:20,left:20,width:56,height:56,borderRadius:'50%',background:'linear-gradient(135deg,#25d366,#128c7e)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 4px 16px rgba(0,0,0,0.25)',textDecoration:'none',zIndex:1000}}>
        <svg viewBox="0 0 24 24" fill="#fff" width="30" height="30" aria-hidden="true">
          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
        </svg>
      </a>

      <div style={{maxWidth:520,margin:'0 auto'}}>

        {/* Header */}
        <div style={{textAlign:'center',marginBottom:14}}>
          <img src="/findre-logo.svg" alt="FindRE" style={{height:60,marginBottom:6}}/>
          <div style={{fontFamily:"'Tajawal',Arial,sans-serif",fontWeight:800,fontSize:18,color:NAVY,letterSpacing:-.5,marginBottom:6}}>FindRE</div>
          <div style={{fontSize:11,color:'#79858d'}}>{isPaidReceipt ? 'النسخة النهائية الموقعة' : 'توقيع فاتورة الإيجار'}</div>
        </div>

        {/* Formal business name — every view */}
        <div style={{textAlign:'center',fontSize:12,color:NAVY,fontWeight:700,marginBottom:14,lineHeight:1.7}}>
          مؤسسة فايندآرإي للعقارات
        </div>

        {/* Link expired view — short-circuit before everything else */}
        {linkExpired ? (
          <div style={{background:'#fff',borderRadius:14,padding:'28px 24px',textAlign:'center',border:'1px solid #f0d99d',marginBottom:14}}>
            <div style={{fontSize:52,marginBottom:12}}>⏰</div>
            <h2 style={{color:'#856404',fontSize:20,marginBottom:8,fontWeight:800}}>انتهت صلاحية الرابط</h2>
            <p style={{color:'#79858d',fontSize:13,lineHeight:1.9}}>
              صلاحية روابط التوقيع 3 أيام فقط. تواصل مع FindRE للحصول على رابط جديد.
            </p>
            <p style={{color:NAVY,fontSize:13,fontWeight:800,marginTop:14}}>📞 0551817337</p>
          </div>
        ) : isPaidReceipt ? (
          <div>
            <div style={{background:'#fff',borderRadius:14,padding:'28px 24px',textAlign:'center',border:'1px solid #d4eecf',marginBottom:14}}>
              <div style={{fontSize:52,marginBottom:12}}>✅</div>
              <h2 style={{color:'#22c55e',fontSize:20,marginBottom:8,fontWeight:800}}>تم استلام الدفع</h2>
              <p style={{color:'#79858d',fontSize:13,lineHeight:1.9,marginBottom:14}}>
                هذه هي النسخة النهائية الموقعة من فاتورتك — يمكنك تحميلها أو حفظها كصورة.
              </p>
              <button onClick={downloadPaidReceipt}
                style={{padding:'12px 28px',borderRadius:10,background:GOLD,border:'none',color:'#fff',fontSize:14,fontWeight:800,cursor:'pointer',fontFamily:"'Tajawal',sans-serif"}}>
                📥 تحميل النسخة النهائية (PDF)
              </button>
            </div>
          </div>
        ) : isWaitingForPay ? (
          <div>
            {/* Status card — no download or preview; FindRE sends the signed PDF after payment is received */}
            <div style={{background:'#fff',borderRadius:14,padding:'28px 24px',textAlign:'center',border:'1px solid #d4eecf',marginBottom:14}}>
              <div style={{fontSize:52,marginBottom:12}}>✅</div>
              <h2 style={{color:'#22c55e',fontSize:20,marginBottom:8,fontWeight:800}}>تم التوقيع بنجاح!</h2>
              <p style={{color:'#79858d',fontSize:13,lineHeight:1.9,marginBottom:6}}>
                {uploadStatus === 'done'      && 'تم إرسال التوقيع إلى FindRE تلقائياً ✓'}
                {uploadStatus === 'uploading' && '⏳ جاري إرسال التوقيع...'}
                {uploadStatus === 'error'     && 'تعذّر الإرسال — يرجى أخذ لقطة شاشة وإرسالها للموظف'}
                {uploadStatus === 'idle'      && 'جاري المعالجة...'}
              </p>
              <p style={{color:'#475569',fontSize:12,lineHeight:1.9,marginTop:14,background:'#faf8f3',border:'1px solid #e8e0cc',borderRadius:10,padding:'10px 14px'}}>
                📄 ستصلك نسخة موقّعة من العقد عبر واتساب بعد إتمام عملية الدفع.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Invoice summary */}
            <div style={{background:'#fff',borderRadius:14,padding:'20px 22px',marginBottom:14,border:'1px solid #e8e0cc'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:14,paddingBottom:12,borderBottom:'1px solid #e8e0cc'}}>
                <div>
                  <div style={{fontSize:11,color:'#79858d',marginBottom:2}}>رقم الفاتورة</div>
                  <div style={{fontSize:16,color:GOLD,fontWeight:800,fontFamily:'monospace'}}>{payload.invoiceNo}</div>
                </div>
                <div style={{textAlign:'left'}}>
                  <div style={{fontSize:11,color:'#79858d',marginBottom:2}}>التاريخ</div>
                  <div style={{fontSize:13,color:NAVY,fontWeight:700,direction:'ltr'}}>{payload.date}</div>
                </div>
              </div>

              <Row k="العميل"        v={payload.fullName}/>
              <Row k="الوحدة"        v={`${payload.unit||''} · ${payload.district||''}`}/>
              <Row k="فترة العقد"    v={`${payload.contractStart||''} → ${payload.contractEnd||''}`} ltr/>
              <Row k="الإيجار الشهري" v={`${fmt(payload.rent)} ر.س`}/>
              {Number(payload.deposit)>0 && <Row k="مبلغ التأمين" v={`${fmt(payload.deposit)} ر.س`}/>}
              <Row k="آلية الدفع"    v={payload.paymentMethod||'—'}/>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0 0',marginTop:8,borderTop:'2px solid '+GOLD}}>
                <div style={{fontSize:13,fontWeight:800,color:NAVY}}>الإجمالي</div>
                <div style={{fontSize:18,fontWeight:800,color:GOLD,direction:'ltr'}}>{fmt(total)} ر.س</div>
              </div>
            </div>

            {/* Anti-scam + verification — shown only on the sign form */}
            <div style={{background:'#f0f7fc',border:'1px solid #b6dbed',borderRadius:10,padding:'10px 14px',fontSize:11,color:'#1e4b6b',marginBottom:12,lineHeight:1.8}}>
              🛡️ <b>وقّع فقط على رابط من FindRE.</b> اتصل في حال وجود أي استفسار قبل التوقيع: <span style={{fontWeight:800,direction:'ltr'}}>0551817337</span>
            </div>

            {/* Expiration notice */}
            {expiresAt && (
              <div style={{textAlign:'center',fontSize:11,color:'#856404',marginBottom:12,fontWeight:700}}>
                ⏰ ينتهي هذا الرابط في {String(expiresAt).slice(0,10)} (صلاحية 3 أيام)
              </div>
            )}

            {/* Late fee notice */}
            <div style={{background:'#fff8e1',border:'1px solid #f0d99d',borderRadius:10,padding:'10px 14px',fontSize:11,color:'#856404',marginBottom:14,lineHeight:1.7}}>
              ⚠️ <b>غرامة التأخير:</b> {fmt(payload.lateDailyRate||100)} ر.س عن كل يوم بعد نهاية العقد.
              <br/>التأمين مسترد بعد الإخلاء وفحص الوحدة.
            </div>

            {/* Signature pad */}
            <div style={{background:'#fff',borderRadius:14,padding:'18px',border:'1px solid #e8e0cc',marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:800,color:NAVY}}>✍️ التوقيع هنا</div>
                <button onClick={clearPad} style={{padding:'5px 12px',fontSize:11,background:'none',border:'1px solid #d9d2c0',color:'#79858d',borderRadius:6,cursor:'pointer',fontFamily:"'Tajawal',sans-serif"}}>مسح</button>
              </div>
              <canvas
                ref={canvasRef}
                width={460} height={200}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                style={{width:'100%',height:200,background:'#faf8f3',border:'2px dashed '+GOLD,borderRadius:8,touchAction:'none',cursor:'crosshair'}}
              />
              <div style={{fontSize:10,color:'#79858d',marginTop:6,textAlign:'center'}}>وقّع بإصبعك أو الفأرة داخل المربع</div>
            </div>

            <button onClick={handleSubmit} disabled={!hasInk}
              style={{width:'100%',padding:'14px',borderRadius:10,background:hasInk?GOLD:'#d9d2c0',border:'none',color:'#fff',fontSize:15,fontWeight:800,cursor:hasInk?'pointer':'not-allowed',fontFamily:"'Tajawal',sans-serif"}}>
              {hasInk ? '✅ تأكيد التوقيع' : 'وقّع أولاً'}
            </button>

            <div style={{textAlign:'center',marginTop:14,fontSize:10,color:'#79858d'}}>
              بالتوقيع أنت توافق على شروط العقد المذكورة أعلاه.
            </div>
          </>
        )}

        {/* ── Bottom footer: FindRE branding + government QR codes for verification ── */}
        <div style={{marginTop:28,paddingTop:18,borderTop:'1px solid #e8e0cc'}}>
          <div style={{display:'flex',justifyContent:'center',gap:14,flexWrap:'wrap',marginBottom:14}}>
            <div style={{textAlign:'center',border:'1px solid #e8e0cc',borderRadius:10,padding:'10px 12px',background:'#fff',minWidth:130}}>
              <div style={{fontSize:9,color:NAVY,fontWeight:700,marginBottom:6}}>السجل التجاري</div>
              <img src="/cr-qr.png" alt="CR QR" style={{width:100,height:100,objectFit:'contain',display:'block',margin:'0 auto'}}/>
            </div>
            <div style={{textAlign:'center',border:'1px solid #e8e0cc',borderRadius:10,padding:'10px 12px',background:'#fff',minWidth:130}}>
              <div style={{fontSize:9,color:NAVY,fontWeight:700,marginBottom:6}}>رخصة فال</div>
              <img src="/fal-qr.png" alt="FAL QR" style={{width:100,height:100,objectFit:'contain',display:'block',margin:'0 auto'}}/>
              <div style={{fontSize:10,color:NAVY,fontWeight:700,marginTop:4,direction:'ltr'}}>1200035498</div>
            </div>
          </div>
          <div style={{textAlign:'center'}}>
            <img src="/findre-logo.svg" alt="FindRE" style={{height:36,marginBottom:4,opacity:0.85}}/>
            <div style={{fontSize:10,color:'#79858d',lineHeight:1.7}}>
              مؤسسة فايند آر إي للعقارات · FindRE Establishment For Real Estate
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
